import React, { useState, useEffect, useMemo, useCallback } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist";
import FilterableTable from "../components/FilterableTable";
import api from "../services/api";
import "./Dashboard.css";

const Plot = createPlotlyComponent(Plotly);

function computeR2(actual, predicted) {
  const pairs = actual
    .map((a, i) => [a, predicted[i]])
    .filter(([a, p]) => a != null && p != null);
  if (pairs.length < 2) return null;
  const n = pairs.length;
  const mean = pairs.reduce((s, [a]) => s + a, 0) / n;
  const ssTot = pairs.reduce((s, [a]) => s + (a - mean) ** 2, 0);
  const ssRes = pairs.reduce((s, [a, p]) => s + (a - p) ** 2, 0);
  if (ssTot === 0) return null;
  return 1 - ssRes / ssTot;
}

function Dashboard({ username, onLogout }) {
  const [csvFiles, setCsvFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [columns, setColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [predicted, setPredicted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => {
    api
      .get("/api/csv-files")
      .then((r) => {
        setCsvFiles(r.data.files);
        if (r.data.files.length) setSelectedFile(r.data.files[0]);
      })
      .catch(() => flash("Failed to load CSV files", "error"));
  }, []);

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const handleRefresh = useCallback(async () => {
    if (!selectedFile) return;
    setLoading(true);
    setPredicted(false);
    try {
      const r = await api.get(`/api/csv-data/${selectedFile}`);
      setColumns(r.data.columns);
      setTableData(r.data.data);
      flash("Data loaded successfully");
    } catch {
      flash("Failed to load CSV data", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  const handlePredict = async () => {
    if (!selectedFile) return;
    setLoading(true);
    try {
      const r = await api.post(`/api/predict/${selectedFile}`);
      setColumns(r.data.columns);
      setTableData(r.data.data);
      setPredicted(true);
      flash("Predictions generated successfully!");
    } catch (err) {
      flash("Prediction failed: " + (err.response?.data?.detail || err.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedFile) return;
    try {
      const r = await api.get(`/api/download/${selectedFile}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `predicted_${selectedFile}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      flash("Download failed", "error");
    }
  };

  const summaryStats = useMemo(() => {
    if (!predicted || !tableData.length) return null;
    const actual = tableData.map((r) => r["Actual Permeability"]).filter((v) => v != null);
    const pred = tableData.map((r) => r["Predicted Permeability"]).filter((v) => v != null);
    if (!pred.length) return null;
    const avg = (a) => a.reduce((s, x) => s + x, 0) / a.length;
    return [
      { label: "Total Rows", value: tableData.length, color: "#4fc3f7" },
      { label: "Avg Actual", value: avg(actual).toFixed(2), color: "#66bb6a" },
      { label: "Avg Predicted", value: avg(pred).toFixed(2), color: "#ffa726" },
      { label: "Min Predicted", value: Math.min(...pred).toFixed(2), color: "#ab47bc" },
      { label: "Max Predicted", value: Math.max(...pred).toFixed(2), color: "#ef5350" },
    ];
  }, [predicted, tableData]);

  const chartTraces = useMemo(() => {
    if (!predicted || !tableData.length) return null;
    const depths = tableData.map((r) => r["Depth"]);
    const actual = tableData.map((r) => r["Actual Permeability"]);
    const pred = tableData.map((r) => r["Predicted Permeability"]);
    const r2 = computeR2(actual, pred);
    return { depths, actual, pred, r2 };
  }, [predicted, tableData]);

  const poissonChart = useMemo(() => {
    if (!predicted || !tableData.length) return null;
    const actual = tableData.map((r) => r["Actual Poisson's Ratio"]);
    const pred = tableData.map((r) => r["Predicted Poisson's Ratio"]);
    if (!actual.some((v) => v != null) || !pred.some((v) => v != null)) return null;

    const valid = actual
      .map((a, i) => [a, pred[i]])
      .filter(([a, p]) => a != null && p != null);
    if (!valid.length) return null;

    const allVals = valid.flat();
    const lo = Math.min(...allVals);
    const hi = Math.max(...allVals);
    const pad = (hi - lo) * 0.05 || 0.01;
    const axisMin = lo - pad;
    const axisMax = hi + pad;
    const r2 = computeR2(actual, pred);

    return {
      actual: valid.map(([a]) => a),
      pred: valid.map(([, p]) => p),
      axisMin,
      axisMax,
      r2,
    };
  }, [predicted, tableData]);

  return (
    <div className="dashboard">
      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo.jpg" alt="Logo" className="sidebar-logo-img" />
          <span className="sidebar-logo-sub">Permeability Prediction</span>
        </div>
        <hr className="sidebar-divider" />

        <p className="sidebar-label">Select Testing CSV</p>
        <select
          className="sidebar-select"
          value={selectedFile}
          onChange={(e) => setSelectedFile(e.target.value)}
        >
          {csvFiles.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <button className="sidebar-btn" onClick={handleRefresh} disabled={loading || !selectedFile}>
          Refresh Preview
        </button>
        <button
          className="sidebar-btn predict"
          onClick={handlePredict}
          disabled={loading || !selectedFile}
        >
          Predict
        </button>
        <button className="sidebar-btn download" onClick={handleDownload} disabled={!predicted}>
          Download Predicted CSV
        </button>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <span>&#128100;</span>
            <span>{username}</span>
          </div>
          <button className="sidebar-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="main-content">
        {message.text && (
          <div className={`toast toast-${message.type}`}>{message.text}</div>
        )}

        <h2 className="section-title">
          {predicted ? "Prediction Results" : "Data Preview"}
        </h2>

        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Processing...</span>
          </div>
        )}

        {!loading && tableData.length > 0 && (
          <FilterableTable columns={columns} data={tableData} />
        )}

        {!loading && tableData.length === 0 && (
          <div className="no-data">
            Select a CSV file and click &quot;Refresh Preview&quot; to load data.
          </div>
        )}

        {summaryStats && (
          <div className="summary-section">
            <h3 className="section-subtitle">Permeability Summary</h3>
            <div className="summary-cards">
              {summaryStats.map((s) => (
                <div key={s.label} className="summary-card">
                  <div className="card-label">
                    <span className="card-dot" style={{ backgroundColor: s.color }} />
                    {s.label}
                  </div>
                  <div className="card-value">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(chartTraces || poissonChart) && (
          <div className="chart-section">
            <div className="charts-row">
              {chartTraces && (
                <div className="chart-panel">
                  <div className="parity-chart-card">
                    <div className="parity-chart-header">
                      <div className="parity-chart-titles">
                        <h3 className="parity-chart-title">Actual vs Predicted Permeability</h3>
                        <p className="parity-chart-subtitle">Permeability profile across depth</p>
                      </div>
                      {chartTraces.r2 != null && (
                        <div className="r2-badge">
                          <span className="r2-label">R²</span>
                          <span className="r2-value">{chartTraces.r2.toFixed(4)}</span>
                        </div>
                      )}
                    </div>
                    <div className="chart-wrap parity-chart-wrap">
                      <Plot
                        data={[
                          {
                            x: chartTraces.depths,
                            y: chartTraces.actual,
                            type: "scatter",
                            mode: "lines+markers",
                            name: "Actual",
                            line: { color: "#60a5fa", width: 2 },
                            marker: { color: "#60a5fa", size: 4 },
                          },
                          {
                            x: chartTraces.depths,
                            y: chartTraces.pred,
                            type: "scatter",
                            mode: "lines+markers",
                            name: "Predicted",
                            line: { color: "#ef4444", width: 2 },
                            marker: { color: "#ef4444", size: 4 },
                          },
                        ]}
                        layout={{
                          paper_bgcolor: "#0f172a",
                          plot_bgcolor: "#0f172a",
                          font: { color: "#f8fafc", family: "Inter, sans-serif", size: 11 },
                          xaxis: {
                            title: "Depth (ft)",
                            gridcolor: "rgba(148, 163, 184, 0.15)",
                            griddash: "dot",
                            zeroline: false,
                            tickfont: { color: "#94a3b8", size: 10 },
                            titlefont: { size: 12, color: "#94a3b8" },
                            linecolor: "#475569",
                            mirror: true,
                          },
                          yaxis: {
                            title: "Permeability",
                            gridcolor: "rgba(148, 163, 184, 0.15)",
                            griddash: "dot",
                            zeroline: false,
                            tickfont: { color: "#94a3b8", size: 10 },
                            titlefont: { size: 12, color: "#94a3b8" },
                            linecolor: "#475569",
                            mirror: true,
                          },
                          legend: {
                            orientation: "h",
                            y: 1.12,
                            x: 1,
                            xanchor: "right",
                            font: { size: 11, color: "#94a3b8" },
                            bgcolor: "transparent",
                          },
                          margin: { l: 56, r: 16, t: 36, b: 56 },
                          autosize: true,
                          height: 380,
                        }}
                        config={{ responsive: true, displayModeBar: false }}
                        style={{ width: "100%", borderRadius: "10px", overflow: "hidden" }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {poissonChart && (
                <div className="chart-panel">
                  <div className="parity-chart-card">
                    <div className="parity-chart-header">
                      <div className="parity-chart-titles">
                        <h3 className="parity-chart-title">Poisson Ratio — Actual vs Predicted</h3>
                        <p className="parity-chart-subtitle">
                          Closer to the dashed line means better prediction
                        </p>
                      </div>
                      {poissonChart.r2 != null && (
                        <div className="r2-badge">
                          <span className="r2-label">R²</span>
                          <span className="r2-value">{poissonChart.r2.toFixed(4)}</span>
                        </div>
                      )}
                    </div>
                    <div className="chart-wrap parity-chart-wrap">
                      <Plot
                        data={[
                          {
                            x: poissonChart.actual,
                            y: poissonChart.pred,
                            type: "scatter",
                            mode: "markers",
                            marker: {
                              color: "rgba(96, 165, 250, 0.6)",
                              size: 8,
                              line: { width: 0 },
                            },
                            hovertemplate:
                              "Actual: %{x:.3f}<br>Predicted: %{y:.3f}<extra></extra>",
                            showlegend: false,
                          },
                          {
                            x: [poissonChart.axisMin, poissonChart.axisMax],
                            y: [poissonChart.axisMin, poissonChart.axisMax],
                            type: "scatter",
                            mode: "lines",
                            line: { color: "#d97706", dash: "dash", width: 2 },
                            hoverinfo: "skip",
                            showlegend: false,
                          },
                        ]}
                        layout={{
                          paper_bgcolor: "#0f172a",
                          plot_bgcolor: "#0f172a",
                          font: { color: "#f8fafc", family: "Inter, sans-serif", size: 11 },
                          xaxis: {
                            title: "Actual Poisson's Ratio",
                            range: [poissonChart.axisMin, poissonChart.axisMax],
                            gridcolor: "rgba(148, 163, 184, 0.15)",
                            griddash: "dot",
                            zeroline: false,
                            tickfont: { color: "#94a3b8", size: 10 },
                            titlefont: { size: 12, color: "#94a3b8" },
                            linecolor: "#475569",
                            mirror: true,
                          },
                          yaxis: {
                            title: "Predicted Poisson's Ratio",
                            range: [poissonChart.axisMin, poissonChart.axisMax],
                            scaleanchor: "x",
                            scaleratio: 1,
                            gridcolor: "rgba(148, 163, 184, 0.15)",
                            griddash: "dot",
                            zeroline: false,
                            tickfont: { color: "#94a3b8", size: 10 },
                            titlefont: { size: 12, color: "#94a3b8" },
                            linecolor: "#475569",
                            mirror: true,
                          },
                          margin: { l: 56, r: 16, t: 16, b: 56 },
                          autosize: true,
                          height: 380,
                        }}
                        config={{ responsive: true, displayModeBar: false }}
                        style={{ width: "100%", borderRadius: "10px", overflow: "hidden" }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
